import { Box } from '@mui/material';
import MentionExtension from '@tiptap/extension-mention';
import Placeholder from '@tiptap/extension-placeholder';
import { useEditor, EditorContent, ReactRenderer } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import type { SuggestionProps, SuggestionKeyDownProps } from '@tiptap/suggestion';
import { forwardRef, useCallback, useEffect, useImperativeHandle, useRef, useState } from 'react';

import { useMentionApi } from '../hooks/useMentionApi';
import { useMentionTypeahead } from '../hooks/useMentionTypeahead';
import { useMentionContext } from '../MentionProvider';
import { MentionSuggestionList } from '../MentionSuggestionList';
import { PermissionWarning as PermissionWarningComponent } from '../PermissionWarning';
import type { ResolvedUser } from '../types';
import { MentionInputProvider, useMentionInputContext } from './MentionInputContext';
import type {
  MentionInputComposableProps,
  MentionEditorProps,
  MentionSuggestionsProps,
} from './types';

/* ------------------------------------------------------------------ */
/*  SuggestionListWrapper — rendered inside TipTap's suggestion popup */
/* ------------------------------------------------------------------ */

interface SuggestionListRef {
  onKeyDown: (props: SuggestionKeyDownProps) => boolean;
}

interface SuggestionListWrapperProps extends SuggestionProps {
  items: ResolvedUser[];
  isLoading: boolean;
  error: string | null;
  onSelectUser: (user: ResolvedUser) => void;
  announceSuggestions: (count: number, belowMinQuery: boolean) => void;
  maxReached: boolean;
  belowMinQuery: boolean;
  minQueryLength: number;
  renderItem?: ((user: ResolvedUser, isSelected: boolean) => React.ReactNode) | undefined;
}

const SuggestionListWrapper = forwardRef<SuggestionListRef, SuggestionListWrapperProps>(
  function SuggestionListWrapper(props, ref) {
    const {
      items,
      isLoading,
      error,
      command,
      onSelectUser,
      announceSuggestions,
      maxReached,
      belowMinQuery,
      minQueryLength,
      renderItem,
    } = props;
    const [selectedIndex, setSelectedIndex] = useState(0);
    const ctx = useMentionInputContext();

    useEffect(() => {
      setSelectedIndex(0);
      announceSuggestions(items.length, belowMinQuery);
    }, [items, belowMinQuery, announceSuggestions]);

    useImperativeHandle(ref, () => ({
      onKeyDown: ({ event }: SuggestionKeyDownProps) => {
        if (event.key === 'ArrowUp') {
          event.preventDefault();
          setSelectedIndex((i) => (i <= 0 ? items.length - 1 : i - 1));
          return true;
        }
        if (event.key === 'ArrowDown') {
          event.preventDefault();
          setSelectedIndex((i) => (i >= items.length - 1 ? 0 : i + 1));
          return true;
        }
        if (event.key === 'Enter' || event.key === 'Tab') {
          event.preventDefault();
          const user = items[selectedIndex];
          if (user) selectUser(user);
          return true;
        }
        return false;
      },
    }));

    const selectUser = (user: ResolvedUser) => {
      if (maxReached) return;
      if (user.hasRecordAccess === false) {
        ctx.setPendingWarning({
          user,
          onConfirm: () => {
            command({ id: user.uuid, label: user.fullName });
            onSelectUser(user);
            ctx.setPendingWarning(null);
          },
        });
        return;
      }
      command({ id: user.uuid, label: user.fullName });
      onSelectUser(user);
    };

    return (
      <MentionSuggestionList
        items={items}
        isLoading={isLoading}
        error={error}
        selectedIndex={selectedIndex}
        onSelect={selectUser}
        renderItem={renderItem}
        belowMinQuery={belowMinQuery}
        minQueryLength={minQueryLength}
      />
    );
  },
);

/* ------------------------------------------------------------------ */
/*  Helper: position floating suggestion popup                         */
/* ------------------------------------------------------------------ */

function updatePosition(props: SuggestionProps, floatingEl: HTMLDivElement) {
  const rect = props.clientRect?.();
  if (!rect) return;
  floatingEl.style.left = `${rect.left}px`;
  floatingEl.style.top = `${rect.bottom + 4}px`;
}

/* ------------------------------------------------------------------ */
/*  Editor sub-component                                               */
/* ------------------------------------------------------------------ */

function Editor({ placeholder = 'Add a comment\u2026', disabled = false, sx }: MentionEditorProps) {
  const ctx = useMentionInputContext();
  const { minQueryLength, currentUserName } = useMentionContext();
  const { items, isLoading, error, search, clear, isRateLimited } = useMentionTypeahead();
  const mentionApiCallbacks = {
    ...(ctx.onMentionCreated != null && { onMentionCreated: ctx.onMentionCreated }),
    ...(ctx.onMentionError != null && { onMentionError: ctx.onMentionError }),
  };
  const { createMention } = useMentionApi(mentionApiCallbacks);

  const liveRegionRef = useRef<HTMLDivElement>(null);
  const componentRef = useRef<ReactRenderer<SuggestionListRef> | null>(null);

  // Tracks whether the active mention query is below the minimum length
  // required by the upstream user-search API. Drives the "type at least N
  // characters" hint instead of a misleading "No users found".
  const [belowMinQuery, setBelowMinQuery] = useState(false);

  // Keep refs in sync for the TipTap suggestion closure
  const itemsRef = useRef(items);
  const isLoadingRef = useRef(isLoading);
  const errorRef = useRef(error);
  const isRateLimitedRef = useRef(isRateLimited);
  const searchRef = useRef(search);
  const clearRef = useRef(clear);
  const minQueryLengthRef = useRef(minQueryLength);
  const belowMinQueryRef = useRef(belowMinQuery);
  const handleSelectUserRef = useRef<((user: ResolvedUser) => void) | undefined>(undefined);
  const mentionCountRef = useRef(ctx.mentionCount);

  itemsRef.current = items;
  isLoadingRef.current = isLoading;
  errorRef.current = error;
  isRateLimitedRef.current = isRateLimited;
  searchRef.current = search;
  clearRef.current = clear;
  minQueryLengthRef.current = minQueryLength;
  belowMinQueryRef.current = belowMinQuery;
  mentionCountRef.current = ctx.mentionCount;

  const announceSuggestions = useCallback((count: number, belowMin: boolean) => {
    if (!liveRegionRef.current) return;
    if (belowMin) {
      liveRegionRef.current.textContent = `Type at least ${String(minQueryLengthRef.current)} character${minQueryLengthRef.current === 1 ? '' : 's'} to search`;
      return;
    }
    liveRegionRef.current.textContent =
      count === 0 ? 'No users found' : `${String(count)} user${count === 1 ? '' : 's'} found`;
  }, []);

  const handleSelectUser = useCallback(
    (user: ResolvedUser) => {
      if (ctx.mentionCount >= ctx.maxMentionsPerComment) {
        ctx.onMentionError?.({
          code: 'MAX_MENTIONS_EXCEEDED',
          message: `Maximum ${String(ctx.maxMentionsPerComment)} mentions per comment`,
        });
        return;
      }

      void createMention(ctx.entityId, {
        sourceProduct: ctx.sourceProduct,
        sourceRecordUrn: ctx.sourceRecordUrn,
        sourceContextType: ctx.sourceContextType,
        sourceContextId: ctx.sourceContextId,
        mentions: [
          {
            mentionedUserId: user.uuid,
            mentionText: currentUserName
              ? `You have been mentioned by ${currentUserName}`
              : user.fullName,
          },
        ],
        deepLinkUrl: ctx.deepLinkUrl,
        ...(ctx.notificationTargetDisplay !== undefined
          ? { notificationTargetDisplay: ctx.notificationTargetDisplay }
          : {}),
      });

      ctx.incrementMentionCount();
    },
    [ctx, createMention],
  );
  handleSelectUserRef.current = handleSelectUser;

  // Keep the ReactRenderer in sync with latest state
  useEffect(() => {
    componentRef.current?.updateProps({
      items,
      isLoading,
      error,
      onSelectUser: handleSelectUser,
      announceSuggestions,
      maxReached: ctx.mentionCount >= ctx.maxMentionsPerComment,
      belowMinQuery,
      minQueryLength,
    });
  }, [
    items,
    isLoading,
    error,
    handleSelectUser,
    announceSuggestions,
    ctx.mentionCount,
    ctx.maxMentionsPerComment,
    belowMinQuery,
    minQueryLength,
  ]);

  const editor = useEditor({
    extensions: [
      StarterKit.configure({
        heading: false,
        bulletList: false,
        orderedList: false,
        blockquote: false,
        codeBlock: false,
        horizontalRule: false,
      }),
      Placeholder.configure({ placeholder }),
      MentionExtension.configure({
        HTMLAttributes: { class: 'mention-chip' },
        renderHTML({ node }) {
          return [
            'span',
            {
              class: 'mention-chip',
              'data-type': 'mention',
              'data-id': node.attrs.id as string,
              'data-label': node.attrs.label as string,
            },
            `@${node.attrs.label as string}`,
          ];
        },
        renderText({ node }) {
          return `@[${node.attrs.label as string}](user:${node.attrs.id as string})`;
        },
        suggestion: {
          char: '@',
          allowSpaces: false,
          startOfLine: false,

          items: ({ query }: { query: string }) => {
            // AC User API v2 requires minimum 3 characters for 'name' parameter search.
            // This minimum is enforced by Access Control and cannot be changed.
            //
            // Below the minimum we surface a "type at least N characters" hint
            // (via `belowMinQuery`) instead of letting the suggestion list
            // render the misleading "No users found" empty state.
            const meetsMin = query.length >= minQueryLengthRef.current;
            if (belowMinQueryRef.current !== !meetsMin) {
              setBelowMinQuery(!meetsMin);
            }
            if (meetsMin && !isRateLimitedRef.current) {
              searchRef.current(query, ctx.entityId, ctx.sourceRecordUrn);
            } else if (!meetsMin) {
              clearRef.current();
            }
            return [];
          },

          render: () => {
            let floatingEl: HTMLDivElement | null = null;

            return {
              onStart: (props: SuggestionProps) => {
                floatingEl = document.createElement('div');
                floatingEl.style.position = 'absolute';
                floatingEl.style.zIndex = '1300';
                document.body.appendChild(floatingEl);

                componentRef.current = new ReactRenderer(SuggestionListWrapper, {
                  props: {
                    ...props,
                    items: itemsRef.current,
                    isLoading: isLoadingRef.current,
                    error: errorRef.current,
                    onSelectUser: handleSelectUserRef.current,
                    announceSuggestions,
                    maxReached: mentionCountRef.current >= ctx.maxMentionsPerComment,
                    belowMinQuery: belowMinQueryRef.current,
                    minQueryLength: minQueryLengthRef.current,
                  },
                  editor: props.editor,
                });

                if (floatingEl && componentRef.current.element) {
                  floatingEl.appendChild(componentRef.current.element);
                }

                updatePosition(props, floatingEl);
              },

              onUpdate: (props: SuggestionProps) => {
                componentRef.current?.updateProps({
                  ...props,
                  items: itemsRef.current,
                  isLoading: isLoadingRef.current,
                  error: errorRef.current,
                  onSelectUser: handleSelectUserRef.current,
                  announceSuggestions,
                  maxReached: mentionCountRef.current >= ctx.maxMentionsPerComment,
                  belowMinQuery: belowMinQueryRef.current,
                  minQueryLength: minQueryLengthRef.current,
                });

                if (floatingEl) {
                  updatePosition(props, floatingEl);
                }
              },

              onKeyDown: (props: SuggestionKeyDownProps) => {
                if (props.event.key === 'Escape') {
                  clearRef.current();
                  return true;
                }
                return componentRef.current?.ref?.onKeyDown(props) ?? false;
              },

              onExit: () => {
                componentRef.current?.destroy();
                componentRef.current = null;
                floatingEl?.remove();
                clearRef.current();
                setBelowMinQuery(false);
              },
            };
          },
        },
      }),
    ],
    editable: !disabled,
    editorProps: {
      attributes: {
        'aria-label': 'Comment input with mention support',
        role: 'textbox',
        'aria-multiline': 'true',
      },
    },
  });

  // Sync editable state when disabled prop changes
  useEffect(() => {
    if (editor && editor.isEditable === disabled) {
      editor.setEditable(!disabled);
    }
  }, [editor, disabled]);

  return (
    <Box className="mention-input" {...(sx != null ? { sx } : {})}>
      <Box
        sx={{
          border: 1,
          borderColor: (t) =>
            t.palette.mode === 'light' ? 'rgba(0, 0, 0, 0.23)' : 'rgba(255, 255, 255, 0.23)',
          borderRadius: 1,
          p: 1.5,
          minHeight: 120,
          bgcolor: 'background.paper',
          cursor: 'text',
          transition: (t) =>
            t.transitions.create(['border-color', 'box-shadow'], { duration: 150 }),
          '&:hover': {
            borderColor: 'text.primary',
          },
          '&:focus-within': {
            borderColor: 'primary.main',
            borderWidth: 2,
            p: 'calc(12px - 1px)', // compensate for 2px border
          },
          '& .ProseMirror': {
            outline: 'none',
            minHeight: 80,
            fontFamily: (t) => t.typography.body1.fontFamily,
            fontSize: (t) => t.typography.body1.fontSize,
            lineHeight: (t) => t.typography.body1.lineHeight,
          },
          '& .ProseMirror p.is-editor-empty:first-child::before': {
            color: 'text.secondary',
            content: 'attr(data-placeholder)',
            float: 'left',
            height: 0,
            pointerEvents: 'none',
          },
        }}
        onClick={() => editor?.commands.focus()}
      >
        <EditorContent editor={editor} />
      </Box>
      <Box
        ref={liveRegionRef}
        aria-live="polite"
        aria-atomic="true"
        sx={{
          position: 'absolute',
          width: 1,
          height: 1,
          p: 0,
          m: -1,
          overflow: 'hidden',
          clip: 'rect(0,0,0,0)',
          whiteSpace: 'nowrap',
          border: 0,
        }}
      />
    </Box>
  );
}

/* ------------------------------------------------------------------ */
/*  Suggestions sub-component (no-op; rendering driven by TipTap)      */
/* ------------------------------------------------------------------ */

function Suggestions(_props: MentionSuggestionsProps) {
  // The suggestion UI is managed by TipTap's suggestion plugin inside the Editor.
  // This sub-component exists for the Composable API surface only.
  return null;
}

/* ------------------------------------------------------------------ */
/*  PermissionWarning sub-component                                    */
/* ------------------------------------------------------------------ */

function MentionPermissionWarning() {
  const ctx = useMentionInputContext();

  if (!ctx.pendingWarning) return null;

  return (
    <PermissionWarningComponent
      userName={ctx.pendingWarning.user.fullName}
      onConfirm={ctx.pendingWarning.onConfirm}
      onCancel={() => ctx.setPendingWarning(null)}
    />
  );
}

/* ------------------------------------------------------------------ */
/*  MentionInputComposable — the root composable component             */
/* ------------------------------------------------------------------ */

export function MentionInputComposable({
  entityId,
  sourceProduct,
  sourceRecordUrn,
  sourceContextType,
  sourceContextId,
  deepLinkUrl,
  notificationTargetDisplay,
  onMentionCreated,
  onMentionError,
  maxMentionsPerComment,
  children,
}: MentionInputComposableProps) {
  return (
    <MentionInputProvider
      entityId={entityId}
      sourceProduct={sourceProduct}
      sourceRecordUrn={sourceRecordUrn}
      sourceContextType={sourceContextType}
      sourceContextId={sourceContextId}
      deepLinkUrl={deepLinkUrl}
      notificationTargetDisplay={notificationTargetDisplay}
      maxMentionsPerComment={maxMentionsPerComment}
      onMentionCreated={onMentionCreated}
      onMentionError={onMentionError}
    >
      {children}
    </MentionInputProvider>
  );
}

MentionInputComposable.Editor = Editor;
MentionInputComposable.Suggestions = Suggestions;
MentionInputComposable.PermissionWarning = MentionPermissionWarning;
